/**
 * buildCooperationColumns —— 合作内容表格的**列装配编排**（列的顺序与组合在一处可读）。
 *
 * 五段，顺序即视觉从左到右：
 *  1. 「是否需包含AI」（custom，见 needAiColumn）
 *  2. 属性列（custom，每属性一列，见 propertyColumns）
 *  3. 指标 / 必要权益 / 期望权益（subArray，列定义由 kpiSubArraySpec / equitySubArraySpecTable 构建）
 *  4. 备注（custom，TextAreaPopover）
 *  5. 操作（custom，fixed:'right'，见 RowActions）
 *
 * 【为何 remark 内联而不单独成文件】它只是一个对象字面量、1 个消费方、无独立心智模型 ——
 * 拆出去只多一跳路径。actions 同理（内容已在 RowActions）。
 *
 * ⚠️ 本文件是**纯函数**，不含 useMemo。调用方（`CooperationTableList.tsx`）负责 useMemo 与
 * **逐字冻结的 15 项依赖数组** —— 见 `scripts/check-deps-array.mjs`。
 * 把依赖数组搬进本文件会破坏该门禁的意义。
 */
import React from 'react';
import { Input } from '@bedrock/components';
import TextAreaPopover from '#components/SForm/SFormElement/TextAreaPropover';
import type { ColumnMeta, SubArraySpec } from '@mpc/common/src/components/ProTableListForm';
import { buildPropertyColumns } from './propertyColumns';
import { buildNeedAiColumn } from './needAiColumn';
import { RowActions, ACTION_COLUMN_WIDTH } from './RowActions';

/** 备注列宽（固定值，与折叠面板 remark 的 maxLength 500 无耦合） */
const REMARK_COLUMN_WIDTH = 160;
/** 备注输入上限（对齐原版 CFormList remark 的 500） */
const REMARK_MAX_LENGTH = 500;

export interface BuildCooperationColumnsParams {
  schema: any;
  subArrays: SubArraySpec<any, any>[];
  structuredVersion?: number;
  updateProperty: (id: number, code: string, v: any) => void;
  updateRow: (id: number, patch: Record<string, any>) => void;
  copyRow: (id: number) => void;
  deleteRow: (id: number) => void;
  applyRowToWholeOrder: (id: number, applyConfig: any) => void;
  /** 「是否需包含AI」列显隐（见 needAiColumn） */
  showNeedAi: boolean;
  view: boolean;
  pinnedCodes: string[];
  togglePin: (code: string) => void;
  forceValidateAll: boolean;
  buildHideOptions: (row: Record<string, any>) => { name: string; hide: boolean }[];
  isSingle: boolean;
  /**
   * ⚠️ 以下两项**不在调用方的 15 项依赖数组里**，这是**有意省略**（与引擎侧 ruleForm 同类）：
   * 它们都是 `useMemoizedFn`（identity 永久稳定），进数组无意义。
   * 见 `scripts/check-deps-array.mjs` 的 intentionalOmissions。
   */
  batchApplyProperty: (code: string, bv: any, mode: 'keep' | 'cover') => void;
  batchApplyRow: (field: string, bv: any, mode: 'keep' | 'cover') => void;
}

export function buildCooperationColumns({
  schema,
  subArrays,
  structuredVersion,
  updateProperty,
  updateRow,
  copyRow,
  deleteRow,
  applyRowToWholeOrder,
  showNeedAi,
  view,
  pinnedCodes,
  togglePin,
  forceValidateAll,
  buildHideOptions,
  isSingle,
  batchApplyProperty,
  batchApplyRow,
}: BuildCooperationColumnsParams): ColumnMeta<any, any>[] {
  const out: ColumnMeta<any, any>[] = [];
  // 行级标量「是否需包含AI」—— 显隐与两条「为何必须是 custom 列」的推导见 ./needAiColumn
  out.push(...buildNeedAiColumn({
    showNeedAi, view, updateRow, forceValidateAll, batchApplyRow,
  }));
  // 属性列：standards + properties 依序，每属性一列（custom，拿整行级联）—— 构建见 ./propertyColumns
  out.push(...buildPropertyColumns({
    schema, structuredVersion, pinnedCodes, togglePin,
    forceValidateAll, updateProperty, view, batchApplyProperty,
  }));
  // 指标/权益 subArray 列
  for (const sub of subArrays) out.push({ kind: 'subArray', sub });
  // 备注（textarea 弹层，maxLength 对齐原版 CFormList remark 的 500）
  out.push({
    kind: 'custom',
    custom: { key: 'remark', title: '备注', width: REMARK_COLUMN_WIDTH, resizable: true, render: (row: Record<string, any>) => (
      <TextAreaPopover value={row.remark ?? ''} view={view} showConfirmButton textAreaProps={{ maxLength: REMARK_MAX_LENGTH }} renderChildren={(v: string) => (
        <Input size="small" value={v} placeholder="请输入" style={{ width: '100%' }} readOnly />
      )} onChange={(v: string) => updateRow(row.id, { remark: v })} />
    ),
    ...(view ? {} : {
      batchEdit: {
        type: 'input',
        render: (v: any, onC: (v: any) => void) => (
          <TextAreaPopover value={v ?? ''} view={view} showConfirmButton textAreaProps={{ maxLength: REMARK_MAX_LENGTH }} renderChildren={(val: string) => (
            <Input size="small" value={val} placeholder="请输入" readOnly />
          )} onChange={onC} />
        ),
        apply: (value: any, mode: 'keep' | 'cover') => batchApplyRow('remark', value, mode),
      },
    }) },
  });
  // 操作列（「字段应用至整单」复用 DropdownListApplyToTheWholeOrder 弹层，列宽相应加宽）
  out.push({
    kind: 'custom',
    custom: { key: 'actions', title: '操作', width: ACTION_COLUMN_WIDTH, fixed: 'right', render: (row: Record<string, any>) => <RowActions id={row.id} row={row} schema={schema} hideOptions={buildHideOptions(row)} isSingle={isSingle} onCopy={copyRow} onDelete={deleteRow} onApplyToWholeOrder={applyRowToWholeOrder} /> },
  });
  return out;
}
